import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createPublicApi } from '../services/api';
import { motion } from 'framer-motion';

const FALLBACKS = [
  'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=600&q=80',
  'https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=600&q=80',
  'https://images.unsplash.com/photo-1445116572660-236099ec97a0?w=600&q=80',
  'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=600&q=80',
  'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=600&q=80',
  'https://images.unsplash.com/photo-1461023058943-07fcbe16d735?w=600&q=80',
];

export const Gallery = () => {
  const { slug } = useParams();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    const pubApi = createPublicApi(slug);
    pubApi.gallery.getAll()
      .then(r => {
        const imgs = r.data.data || [];
        if (imgs.length === 0) {
          setImages(FALLBACKS.map((url, i) => ({ id: i, image_url: url, caption: 'SwadZone' })));
        } else {
          setImages(imgs);
        }
      })
      .catch(() => {
        // On any error, show fallback images so page doesn't break
        setImages(FALLBACKS.map((url, i) => ({ id: i, image_url: url, caption: 'SwadZone' })));
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="bg-bark-900 py-16 mb-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl font-bold text-cream-100 mb-4">Our Gallery</motion.h1>
          <p className="text-bark-400">A visual story of flavors, moments, and memories</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {Array(8).fill(0).map((_, i) => (
              <div key={i} className={`skeleton rounded-2xl w-full ${i % 3 === 0 ? 'h-64' : i % 3 === 1 ? 'h-48' : 'h-80'} break-inside-avoid`} />
            ))}
          </div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {images.map((img, i) => (
              <motion.div key={img.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(i * 0.05, 0.5) }}
                className="break-inside-avoid rounded-2xl overflow-hidden group cursor-zoom-in relative"
              >
                <img
                  src={img.image_url.startsWith('http') ? img.image_url : `http://localhost:5000${img.image_url}`}
                  alt={img.caption || 'Gallery'}
                  className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={e => { e.target.src = FALLBACKS[i % FALLBACKS.length]; }}
                />
                {img.caption && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs font-medium">{img.caption}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const About = () => {
  const { slug } = useParams();
  const DEFAULT_HERO = 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1400&q=80';
  const DEFAULT_CAFE = 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=800&q=80';

  const [s, setS] = React.useState({
    about_hero_url: '',
    hero_bg_url: '',
    about_established: '2016',
    about_heading: 'A Cup of Passion, A Heart of Community',
    about_story1: 'SwadZone was born from a simple dream: to create a space where the aroma of freshly brewed coffee blends with the warmth of genuine human connection. What started as a tiny corner shop has grown into a beloved neighborhood institution.',
    about_story2: "Every bean is sourced directly from sustainable farms. Every recipe is crafted with intention. Every visit is treated as an opportunity to create a memory. That's the Aroma promise.",
    about_cafe_img_url: '',
    team1_name:'Marco Rossi',  team1_role:'Head Barista',   team1_bio:'15 years of coffee mastery across Italy and New York.', team1_photo_url:'',
    team2_name:'Priya Patel',  team2_role:'Executive Chef', team2_bio:'Trained in Paris, bringing global flavors to every dish.',  team2_photo_url:'',
    team3_name:'James Wong',   team3_role:'Pastry Artist',  team3_bio:"Creates edible art that's almost too beautiful to eat.",  team3_photo_url:'',
  });

  React.useEffect(() => {
    if (!slug) return;
    const pubApi = createPublicApi(slug);
    pubApi.settings.get().then(r => {
      const d = r.data.data || {};
      setS(prev => ({ ...prev, ...d }));
    }).catch(() => {});
  }, [slug]);

  const team = [1,2,3]
    .map(n => ({ name:s[`team${n}_name`], role:s[`team${n}_role`], bio:s[`team${n}_bio`], photoUrl:s[`team${n}_photo_url`] }))
    .filter(m => m.name);

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero */}
      <div className="relative bg-bark-900 py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url(${s.about_hero_url || s.hero_bg_url || DEFAULT_HERO})`,
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}/>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl font-bold text-cream-100 mb-4">Our Story</motion.h1>
          <p className="text-bark-400 text-lg max-w-xl mx-auto">Born from a passion for exceptional coffee and genuine hospitality.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Story section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <p className="text-espresso-500 font-medium text-sm uppercase tracking-widest mb-3">
              Est. {s.about_established || '2016'}
            </p>
            <h2 className="section-title mb-6">{s.about_heading || 'A Cup of Passion, A Heart of Community'}</h2>
            {s.about_story1 && <p className="text-bark-600 leading-relaxed mb-4">{s.about_story1}</p>}
            {s.about_story2 && <p className="text-bark-600 leading-relaxed">{s.about_story2}</p>}
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="rounded-3xl overflow-hidden h-96">
            <img src={s.about_cafe_img_url || DEFAULT_CAFE} alt="Cafe" className="w-full h-full object-cover" />
          </motion.div>
        </div>

        {/* Team */}
        {team.length > 0 && (
          <>
            <div className="text-center mb-12">
              <p className="text-espresso-500 font-medium text-sm uppercase tracking-widest mb-2">The People</p>
              <h2 className="section-title">Meet Our Team</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {team.map((m, i) => (
                <motion.div key={m.name}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="card p-8 text-center">
                  {m.photoUrl ? (
                    <img src={m.photoUrl} alt={m.name}
                      className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
                      style={{ border: '3px solid #f0d0b0' }}/>
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-espresso-100 flex items-center justify-center mx-auto mb-4">
                      <span className="font-display text-3xl font-bold text-espresso-500">{m.name[0]}</span>
                    </div>
                  )}
                  <h3 className="font-display text-xl font-bold text-primary mb-1">{m.name}</h3>
                  <p className="text-espresso-500 text-sm font-medium mb-3">{m.role}</p>
                  <p className="text-bark-600 text-sm">{m.bio}</p>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const Contact = () => {
  const { slug } = useParams();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [contactInfo, setContactInfo] = React.useState({
    address: '123 Coffee Lane, Brew District, NY 10001',
    phone: '+1 (555) 123-4567',
    email: 'hello@aromacafe.com',
    hours: '{contactInfo.hours}',
  });

  React.useEffect(() => {
    if (!slug) return;
    const pubApi = createPublicApi(slug);
    pubApi.settings.get().then(r => {
      const d = r.data.data || {};
      setContactInfo({
        address:       d.contact_address || '123 Coffee Lane, Brew District, NY 10001',
        phone:         d.contact_phone   || '+1 (555) 123-4567',
        email:         d.contact_email   || 'hello@aromacafe.com',
        hours:         d.contact_hours   || 'Mon–Fri 7am–10pm · Sat–Sun 8am–11pm',
        map_embed_url: d.map_embed_url   || '',
      });
    }).catch(() => {});
  }, [slug]);

  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      const pubApi = createPublicApi(slug);
      await pubApi.contact.send({ name: form.name, email: form.email, message: form.message });
      setSent(true);
    } catch {
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="bg-bark-900 py-16 mb-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl font-bold text-cream-100 mb-4">Get In Touch</motion.h1>
          <p className="text-bark-400">We'd love to hear from you</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="card p-8 mb-6">
              <h3 className="font-display text-2xl font-bold text-primary mb-6">Contact Information</h3>
              <div className="space-y-4">
                {[
                  { icon: '📍', label: 'Address', value: contactInfo.address },
                  { icon: '📞', label: 'Phone',   value: contactInfo.phone   },
                  { icon: '✉️', label: 'Email',   value: contactInfo.email   },
                  { icon: '🕐', label: 'Hours', value: 'Mon–Fri 7am–10pm · Sat–Sun 8am–11pm' },
                ].map(item => (
                  <div key={item.label} className="flex gap-4">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <p className="text-xs text-bark-500 uppercase tracking-wide">{item.label}</p>
                      <p className="font-medium text-primary">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Map embed */}
            <div className="rounded-2xl overflow-hidden h-64 border border-bark-200">
              {contactInfo.map_embed_url ? (
                <iframe src={contactInfo.map_embed_url} width="100%" height="100%" style={{border:0,display:'block'}} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Location Map"/>
              ) : (
                <div className="h-full bg-bark-100 flex items-center justify-center">
                  <div className="text-center text-bark-400">
                    <div className="text-4xl mb-2">🗺️</div>
                    <p className="text-sm">Map not set. Admin se configure karein.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card p-8">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
                className="text-center py-10"
              >
                {/* Icon circle with expanding orange glow rings */}
                <div className="relative flex items-center justify-center mx-auto mb-7" style={{ width: 110, height: 110 }}>
                  {[0, 1, 2].map(i => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0.8, opacity: 0.6 }}
                      animate={{ scale: 2.8, opacity: 0 }}
                      transition={{ duration: 2.4, delay: i * 0.7, repeat: Infinity, ease: 'easeOut' }}
                      style={{
                        position: 'absolute', width: 90, height: 90, borderRadius: '50%',
                        border: '2px solid #c96428',
                        boxShadow: '0 0 12px rgba(201,100,40,0.4)',
                      }}
                    />
                  ))}
                  <motion.div
                    initial={{ scale: 0, rotate: -15, opacity: 0 }}
                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                    transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    style={{
                      width: 90, height: 90, borderRadius: '50%',
                      background: 'linear-gradient(145deg, #d97234, #9a4a1e)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 38, boxShadow: '0 12px 40px rgba(201,100,40,0.35)',
                      zIndex: 1, position: 'relative',
                    }}
                  >
                    📨
                  </motion.div>
                </div>

                {/* Success badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.55, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: '#f0fdf4', border: '1.5px solid #86efac',
                    borderRadius: 99, padding: '5px 16px', marginBottom: 18,
                    fontSize: 13, fontWeight: 700, color: '#16a34a',
                  }}
                >
                  ✓ Message Sent
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.72, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="font-display text-3xl font-bold mb-3"
                  style={{ color: '#1a0f00' }}
                >
                  Thank you, {form.name}!
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.88, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                  style={{ color: '#8b6347', fontSize: 15, lineHeight: 1.7, marginBottom: 32 }}
                >
                  We've received your message and will<br />
                  reply to <strong style={{ color: '#c96428' }}>{form.email}</strong> within 24 hours. ☕
                </motion.p>

                {/* Soft breathing dots */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.0, duration: 0.6 }}
                  style={{ display: 'flex', justifyContent: 'center', gap: 5, marginBottom: 30 }}
                >
                  {[0, 1, 2].map(i => (
                    <motion.div
                      key={i}
                      animate={{ opacity: [0.25, 1, 0.25] }}
                      transition={{ delay: i * 0.3, duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                      style={{ width: 6, height: 6, borderRadius: '50%', background: '#c96428' }}
                    />
                  ))}
                </motion.div>

                <motion.button
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ scale: 1.03, boxShadow: '0 8px 24px rgba(201,100,40,0.35)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => { setSent(false); setForm({ name: '', email: '', message: '' }); }}
                  className="btn-primary"
                  style={{ padding: '12px 32px', fontSize: 14, transition: 'box-shadow 0.3s' }}
                >
                  Send Another Message
                </motion.button>
              </motion.div>
            ) : (
              <>
                <h3 className="font-display text-2xl font-bold text-primary mb-6">Send a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-bark-700 mb-1">Name *</label>
                    <input className="input-field" required placeholder="Your name"
                      value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-bark-700 mb-1">Email *</label>
                    <input type="email" className="input-field" required placeholder="your@email.com"
                      value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-bark-700 mb-1">Message *</label>
                    <textarea className="input-field h-40 resize-none" required placeholder="How can we help you?"
                      value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
                  </div>
                  <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={sending} className="btn-primary w-full py-4">
                    {sending ? '⏳ Sending...' : 'Send Message'}
                  </motion.button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
